/**
 * ScheduleWizardScreen — Path B: preview and confirm a generated schedule.
 *
 * Shown after SchedulingScreen when a templateId is available. The user
 * sees the generated timeline, can adjust preferences, and confirms to
 * save the schedule.
 *
 * UI-INV-005: Leaf component, props only, no engine imports.
 */
import { useState } from "react";
import type { EditableRetreatSchedule } from "@retreat/core";
import type { WakeOffset } from "../constants";
import { useScheduleGenerator } from "../hooks/useScheduleGenerator";
import { getTemplate } from "@retreats/content";
import { ScheduleTimeline } from "../components/ScheduleTimeline";
import { AdjustPreferencesSheet } from "../components/AdjustPreferencesSheet";
import { MetaStrip } from "../components/MetaStrip";
import { PrimaryAction } from "../primitives/PrimaryAction";
import { TEST_IDS } from "../testing/testIds";
import styles from "./ScheduleWizardScreen.module.css";

type Props = {
  templateId: string;
  scheduledAt: number;
  wakeOffset: WakeOffset;
  onConfirm: (generatedSchedule: EditableRetreatSchedule) => void;
  onBack: () => void;
};

export function ScheduleWizardScreen({
  templateId,
  scheduledAt,
  wakeOffset: _wakeOffset,
  onConfirm,
  onBack,
}: Props) {
  const [adjustOpen, setAdjustOpen] = useState(false);

  const startDateTime = new Date(scheduledAt);
  const { schedule, warnings, preferences, regenerate } = useScheduleGenerator(
    templateId,
    startDateTime,
  );

  // Get template metadata for noble silence + tradition info
  let template = null;
  try {
    template = getTemplate(templateId);
  } catch {
    // unknown templateId — render minimal fallback
  }

  const tradition = (template?.tradition ?? "zen") as "zen" | "vipassana";
  const defaultWakeHour = template?.defaultWakeHour ?? 5;
  const hasNoSilence = template?.noblesilence === true;

  return (
    <div className={styles.screen} data-testid={TEST_IDS.SCHEDULE_WIZARD_SCREEN}>
      <MetaStrip label="Review schedule" onBack={onBack} />

      <div className={styles.content}>
        <h1 className={styles.heading}>Your retreat schedule</h1>

        {/* Noble silence notice */}
        {hasNoSilence && (
          <div className={styles.noSilenceBlock}>
            <span className={styles.noSilenceIcon}>🔇</span>
            <span>Noble silence throughout</span>
          </div>
        )}

        {/* Warning banner */}
        {warnings.length > 0 && (
          <div
            className={styles.warningBanner}
            data-testid={TEST_IDS.SCHEDULE_WIZARD_WARNING_BANNER}
          >
            <div className={styles.warningTitle}>⚠ Timing notes</div>
            <ul className={styles.warningList}>
              {warnings.map((w) => (
                <li key={w.containerId}>{w.message}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Adjust button */}
        <div className={styles.adjustRow}>
          <button
            className={styles.adjustBtn}
            onClick={() => setAdjustOpen(true)}
            type="button"
            data-testid={TEST_IDS.SCHEDULE_WIZARD_ADJUST_BTN}
          >
            Adjust times
          </button>
        </div>

        {/* Timeline */}
        <div data-testid={TEST_IDS.SCHEDULE_WIZARD_TIMELINE}>
          <ScheduleTimeline
            schedule={schedule}
            startDateTime={startDateTime}
            warnings={warnings}
          />
        </div>
      </div>

      {/* CTA */}
      <div className={styles.ctas}>
        <PrimaryAction
          onClick={() => onConfirm(schedule)}
          testId={TEST_IDS.SCHEDULE_WIZARD_CONFIRM}
        >
          Confirm schedule
        </PrimaryAction>
      </div>

      {/* Adjust preferences sheet */}
      <AdjustPreferencesSheet
        open={adjustOpen}
        preferences={preferences}
        tradition={tradition}
        defaultWakeHour={defaultWakeHour}
        isStartNowMode={false}
        onPreferencesChange={regenerate}
        onClose={() => setAdjustOpen(false)}
      />
    </div>
  );
}
